/**
 * ITER296.2 · Ticket Settings — Routing Rules + Priority SLA.
 * Admin-only. Routing rules are evaluated top-down (first match wins);
 * SLA windows drive due_at + escalation for every new ticket.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, Trash2, RefreshCw, Save, GripVertical, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { SectionBand, SectionTitle } from '../components/ops/SectionBand';

const API = process.env.REACT_APP_BACKEND_URL;

const SOURCE_DEPTS = [
  'Kitchen', 'Housekeeping', 'Bar', 'Restaurant', 'Room',
  'Service', 'Stores', 'Security', 'IT', 'Accounts', 'Admin',
];
const ASSIGN_DEPTS = [
  'Maintenance', 'Housekeeping', 'Kitchen', 'Service', 'Restaurant',
  'Bar', 'Stores', 'Accounts', 'IT', 'Admin', 'Security',
];

export default function AdminTicketSettings() {
  const { getHeaders } = useAuth();
  const [rules, setRules] = useState([]);
  const [sla, setSla] = useState({ critical: 4, high: 12, medium: 24, low: 48 });
  const [loading, setLoading] = useState(true);
  const [savingSla, setSavingSla] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesR, slaR] = await Promise.all([
        axios.get(`${API}/api/admin/ticket-routing-rules`, { headers: getHeaders() }),
        axios.get(`${API}/api/admin/ticket-sla-config`,    { headers: getHeaders() }),
      ]);
      setRules(rulesR.data || []);
      setSla(slaR.data || sla);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load ticket settings');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getHeaders]);
  useEffect(() => { load(); }, [load]);

  const addRule = () => {
    const draft = {
      _draft: true,
      id: `draft-${Date.now()}`,
      source_department: 'Kitchen',
      keywords: '',
      assigned_department: 'Maintenance',
      assigned_role: 'maintenance',
      enabled: true,
    };
    setRules((prev) => [...prev, draft]);
  };

  const saveRule = async (r, idx) => {
    try {
      const body = {
        source_department:   r.source_department,
        keywords:            r.keywords || '',
        assigned_department: r.assigned_department,
        assigned_role:       r.assigned_role || '',
        enabled:             r.enabled !== false,
        order:               r.order != null ? r.order : idx,
      };
      let res;
      if (r._draft) {
        res = await axios.post(`${API}/api/admin/ticket-routing-rules`, body,
                                { headers: getHeaders() });
      } else {
        res = await axios.patch(`${API}/api/admin/ticket-routing-rules/${r.id}`, body,
                                 { headers: getHeaders() });
      }
      const saved = res.data;
      setRules((prev) => prev.map((x, i) => (i === idx ? saved : x)));
      toast.success('Rule saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save rule');
    }
  };

  const deleteRule = async (r, idx) => {
    if (r._draft) {
      setRules((prev) => prev.filter((_, i) => i !== idx));
      return;
    }
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`${API}/api/admin/ticket-routing-rules/${r.id}`,
                          { headers: getHeaders() });
      setRules((prev) => prev.filter((_, i) => i !== idx));
      toast.success('Rule deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const updateRule = (idx, patch) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch, _dirty: true } : r)));
  };

  const saveSla = async () => {
    setSavingSla(true);
    try {
      const body = {
        critical: Number(sla.critical),
        high:     Number(sla.high),
        medium:   Number(sla.medium),
        low:      Number(sla.low),
      };
      const res = await axios.put(`${API}/api/admin/ticket-sla-config`, body,
                                    { headers: getHeaders() });
      setSla(res.data);
      toast.success('SLA windows saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save SLA');
    } finally { setSavingSla(false); }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="ticket-settings-page">
      <SectionBand
        title="Ticket Settings"
        subtitle="Configure routing rules and priority-based SLA windows. Rules are evaluated top-down — the first match wins."
        icon={ClipboardList}
        testId="ticket-settings-page-band"
        actions={
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}
                   className="text-white/90 hover:text-white hover:bg-white/10"
                   data-testid="refresh-settings">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        }
      />

      {/* SLA Windows */}
      <SectionTitle title="Priority SLA Windows" icon={Zap} testId="band-sla" />
      <div className="bg-white border border-[#E2E8DF] rounded-lg p-4 mb-6"
           data-testid="sla-config-card">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-[#5C6B64]">
            Hours from ticket creation until it becomes overdue. Critical tickets escalate immediately.
          </p>
          <Button onClick={saveSla} disabled={savingSla}
                  className="bg-[color:var(--color-primary)] hover:bg-[color:var(--color-primary-hover)] text-white"
                  data-testid="save-sla-btn">
            <Save className="w-4 h-4 mr-2" /> {savingSla ? 'Saving…' : 'Save SLA'}
          </Button>
        </div>
        <div className="grid grid-cols-4 gap-3">
          {['critical', 'high', 'medium', 'low'].map((k) => (
            <div key={k}>
              <Label className="text-[10px] uppercase tracking-wider text-[#5C6B64] mb-1 block capitalize">
                {k}
              </Label>
              <div className="flex items-center gap-1">
                <Input
                  type="number"
                  min="1"
                  max="720"
                  value={sla[k]}
                  onChange={(e) => setSla((prev) => ({ ...prev, [k]: e.target.value }))}
                  className="text-center"
                  data-testid={`sla-${k}-input`}
                />
                <span className="text-xs text-[#5C6B64]">h</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Routing Rules */}
      <SectionTitle
        title="Routing Rules"
        subtitle="Top-down; first match wins. Empty keywords = wildcard for that source department."
        icon={ClipboardList}
        testId="band-routing-rules"
        actions={
          <Button onClick={addRule} size="sm"
                  className="bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]"
                  data-testid="add-routing-rule">
            <Plus className="w-4 h-4 mr-1" /> Add Rule
          </Button>
        }
      />
      <div className="bg-white border border-[#E2E8DF] rounded-lg p-4"
           data-testid="routing-rules-card">

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="routing-rules-table">
            <thead className="bg-[color:var(--color-primary)] text-white">
              <tr className="text-left text-[10px] uppercase tracking-[0.14em]">
                <th className="px-3 py-2 w-10">#</th>
                <th className="px-3 py-2">Source Dept</th>
                <th className="px-3 py-2">Keywords (comma-separated)</th>
                <th className="px-3 py-2">→ Assigned Dept</th>
                <th className="px-3 py-2">Assigned Role</th>
                <th className="px-3 py-2 w-20">Enabled</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && !loading && (
                <tr><td colSpan={7} className="text-center py-6 text-[#5C6B64] text-xs">
                  No routing rules yet — add your first one above.
                </td></tr>
              )}
              {rules.map((r, i) => (
                <tr key={r.id} className={`border-b border-[#E2E8DF] ${r._dirty ? 'bg-amber-50' : ''}`}
                    data-testid={`routing-rule-row-${i}`}>
                  <td className="px-3 py-2 text-xs text-[#5C6B64] font-mono">{i + 1}</td>
                  <td className="px-3 py-2">
                    <Select value={r.source_department}
                            onValueChange={(v) => updateRule(i, { source_department: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`rule-source-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SOURCE_DEPTS.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Input value={r.keywords || ''}
                           onChange={(e) => updateRule(i, { keywords: e.target.value })}
                           placeholder="(any)"
                           className="h-8 text-xs"
                           data-testid={`rule-keywords-${i}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Select value={r.assigned_department}
                            onValueChange={(v) => updateRule(i, { assigned_department: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`rule-assigned-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGN_DEPTS.map((d) => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Input value={r.assigned_role || ''}
                           onChange={(e) => updateRule(i, { assigned_role: e.target.value })}
                           placeholder="role_id"
                           className="h-8 text-xs"
                           data-testid={`rule-role-${i}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Switch checked={r.enabled !== false}
                            onCheckedChange={(v) => updateRule(i, { enabled: v })}
                            data-testid={`rule-enabled-${i}`} />
                  </td>
                  <td className="px-3 py-2 flex gap-1">
                    <Button size="sm" variant="outline"
                            onClick={() => saveRule(r, i)}
                            disabled={!r._draft && !r._dirty}
                            data-testid={`rule-save-${i}`}>
                      <Save className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => deleteRule(r, i)}
                            data-testid={`rule-delete-${i}`}>
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
